import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.7, delay, ease }}
    className={className}
  >
    {children}
  </motion.div>
);

const Section = ({ title, children, dark = false, className = "" }) => (
  <section className={`${dark ? "bg-zinc-950 text-white" : "bg-white"} py-20 md:py-28 ${className}`}>
    <div className="max-w-3xl mx-auto px-6 md:px-12">
      <Reveal>
        <h2 className={`font-display text-3xl md:text-4xl font-extrabold tracking-tighter leading-tight ${dark ? "text-white" : "text-zinc-900"}`}>
          {title}
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <div className={`mt-6 space-y-5 text-base md:text-lg leading-relaxed ${dark ? "text-white/70" : "text-zinc-600"}`}>
          {children}
        </div>
      </Reveal>
    </div>
  </section>
);

export default function About() {
  const navigate = useNavigate();
  return (
    <div data-testid="about-page" className="bg-white">
      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-16 md:pb-24 bg-[#F5F5F7]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-4">Our story</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-zinc-900 leading-[1.05]">
              Created to Help Families Feel More Connected
            </h1>
            <p className="mt-6 text-base md:text-lg text-zinc-500 max-w-2xl mx-auto leading-relaxed">
              Vsense began with a simple but deeply personal concern: parents deserve a better way to
              stay connected to the people they love.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Image band */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 -mt-10 md:-mt-16">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
            {[
              { src: "/products/about-hero.jpg", alt: "A grandmother and granddaughter smiling together in a park, both wearing Vsense smart glasses" },
              { src: "/products/about-2.jpg", alt: "A grandfather and grandson smiling together in a park, both wearing Vsense smart glasses" },
              { src: "/products/about-3.jpg", alt: "A mother and daughter sharing a moment in a park, the daughter wearing Vsense smart glasses" },
            ].map((img, i) => (
              <Reveal key={img.src} delay={i * 0.08}>
                <div className="rounded-[2rem] overflow-hidden aspect-[2/3] shadow-[0_30px_80px_rgb(0,0,0,0.10)]">
                  <img src={img.src} alt={img.alt} className="w-full h-full object-cover" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Founder intro */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="space-y-5 text-base md:text-lg text-zinc-600 leading-relaxed">
              <p>
                Every day, families hear troubling stories involving missing children, child trafficking,
                bullying, and other situations that no parent wants to imagine. At the same time, many
                schools are restricting or banning phones, making it more difficult for parents to reach
                their children during the school day.
              </p>
              <p>
                I wanted to create something that could offer families another layer of connection and
                reassurance without requiring a child to carry a traditional smartphone or be introduced
                to social media and unrestricted internet access too early.
              </p>
              <p className="font-semibold text-zinc-900">That idea became Vsense.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Helping Children Stay Connected */}
      <Section title="Helping Children Stay Connected" dark>
        <p>
          Vsense smart glasses are being designed to help parents and guardians stay connected to their
          children through one simple app.
        </p>
        <p>
          Depending on the available model and settings, families can access features such as live video
          and audio, real-time location updates, two-way voice communication, voice-activated calling,
          navigation assistance, and emergency calling.
        </p>
        <p>
          The goal is to give children more independence while helping their parents feel less
          disconnected from what is happening around them.
        </p>
        <p>
          Vsense is not intended to replace responsible parenting, school safety procedures, or emergency
          services. It is designed to provide families with an additional way to communicate and stay
          informed when a phone may not be available.
        </p>
        <p>
          Use of Vsense should always follow school policies and applicable privacy and recording laws.
        </p>
      </Section>

      {/* Connection Without Early Internet Exposure */}
      <Section title="Connection Without Early Internet Exposure">
        <p>
          Many parents want their children to be reachable without giving them unrestricted access to the
          internet, social media, and everything that comes with owning a smartphone.
        </p>
        <p>
          Vsense is being created as a more focused alternative. Instead of placing the entire internet in
          a child's hands, it centers on the features families may need most: communication, location
          assistance, navigation, and emergency access.
        </p>
      </Section>

      {/* Supporting Older Adults and Caregivers */}
      <Section title="Supporting Older Adults and Caregivers" dark>
        <p>
          The adult version of Vsense was inspired by families caring for older adults with dementia,
          memory loss, or conditions that make it difficult for them to safely navigate daily life
          independently.
        </p>
        <p>
          Caregiving can bring constant worry, especially when a loved one becomes confused, gets lost, or
          cannot easily explain where they are. Vsense is being designed to help caregivers remain
          connected, communicate with their loved ones, and access useful location information when
          assistance may be needed.
        </p>
        <p>
          It is not a replacement for professional care, medical treatment, or direct supervision. It is
          intended to be an additional connection between caregivers and the people who depend on them.
        </p>
      </Section>

      {/* Our Mission */}
      <Section title="Our Mission">
        <p>
          Our mission is to create technology that feels protective without feeling intrusive, useful
          without being complicated, and supportive without taking away a person's independence.
        </p>
        <p>
          Vsense is more than a pair of smart glasses. It represents greater connection, greater
          awareness, and a little more peace of mind for families caring for the people they love most.
        </p>
        <p className="text-base text-zinc-500 leading-relaxed">
          We're a small team of engineers, designers and parents who believe safety shouldn't feel like
          surveillance. So we obsess over the details: featherlight frames people actually want to wear,
          an app that shows only what matters, and an on-device assistant that keeps data close to home.
        </p>
      </Section>

      {/* CTA — VIP Early Access */}
      <section className="bg-[#F5F5F7] py-24 md:py-28">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter text-zinc-900">
              Join Vsense VIP Early Access
            </h2>
            <p className="mt-5 text-base md:text-lg text-zinc-500 leading-relaxed">
              Join the Vsense VIP Early Access list to receive updates before public preorders open. No
              payment or credit card is required.
            </p>
            <p className="mt-4 text-base md:text-lg text-zinc-500 leading-relaxed">
              VIP members will receive a private preorder link and a 40%-off launch offer when Vsense
              becomes available.
            </p>
            <button
              data-testid="about-cta-reserve"
              onClick={() => navigate("/shop")}
              className="mt-9 inline-flex items-center gap-2 bg-zinc-900 text-white text-sm font-semibold px-8 py-3.5 rounded-full hover:bg-zinc-800 active:scale-[0.98] transition-all duration-200"
            >
              Join VIP — Free <ArrowRight className="w-4 h-4" />
            </button>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
